/**
 * Game data sources for the video-ad GAME BROWSER, read through spark-creatives (server-side, service
 * token) instead of the browser hitting the platform data cluster directly.
 *
 * WHY NOT the config-engine `fetchSources` thunk: that GETs `${dataSourceApiUrl}/resolve/sources` with the
 * per-brand GUEST token straight from the browser. On prod that 401s, and because it is a guest-token read
 * (never the owner session) the global auth-interceptor mis-reads the 401 as an owner-session expiry and
 * signs the owner out. Spark already does this exact read for banners with its own SERVICE token, cross
 * cluster; `GET /creatives/tenant/{id}/data-source` relays the raw upstream blob the same way, so the game
 * adapters (`game-sources.ts`) read `source.data` unchanged.
 *
 * Two shapes of the catalogue (`all_slot_games_api`):
 *  - `view=slim`: every game with only what the browse screens filter and name by (id, name, provider_id and the
 *    type / subtype / visual-theme attributes), no art. This is what the screens hold.
 *  - `ids=a,b,c`: the FULL records (art bundle included) of those games, in the order asked, an unknown id simply
 *    absent. `fetchGamesByIds` reads these, batched, for whatever is drawn.
 * A spark from before both ignores them and answers the full catalogue: everything here still works against it, it
 * only downloads more.
 *
 * The results are held in module-level caches so the catalogue is fetched at most once per (brand, source, view)
 * across the many conditional remounts of the picker, and each game's full record at most once per brand;
 * concurrent callers share one request.
 */
import axios, { type AxiosRequestConfig } from 'axios';

import { GAME_CATALOG_SOURCE_ID, gameKey, gamesOfSource, type GameSourceRecord } from './game-sources';

/** The raw upstream data-source blob (`{ element_type, meta, data }`) the game adapters read. Loose on
 *  purpose: only `data` is consumed (by gamesOfSource / gameIdsOfSource). */
export type GameSourceBlob = { data?: unknown; [key: string]: unknown };

/** A narrower shape of the catalogue. `'slim'`: every game with only id, name, provider_id and the
 *  `metadata_game_attribute_game_type` / `_game_subtype` / `_visual_theme` attributes (a missing one may be null; the
 *  theme stays raw, a string or a one-element array), in the full catalogue's order. No art. */
export type GameSourceView = 'slim';

export interface GameSourceOptions {
  /** Only the catalogue takes a view (the service refuses one on any other source); unset is the full read. */
  view?: GameSourceView;
}

/** At most this many ids go in one by-id read: the service refuses more. */
export const GAME_IDS_PER_READ = 200;

/** At most this many by-id reads are out at once, every brand's together. */
export const GAME_ID_READS_AT_ONCE = 3;

/** The ids the service accepts in `ids`. Any other id fails the WHOLE read (400), so it is never sent: it reads as a
 *  game the catalogue does not hold. */
const GAME_ID_TOKEN = /^[A-Za-z0-9_-]{1,32}$/;

const creativesBase = (): string => {
  const cfg = window.config as unknown as Record<string, unknown>;
  return typeof cfg?.creativesApiUrl === 'string' ? (cfg.creativesApiUrl as string).replace(/\/+$/, '') : '';
};

// A SEPARATE service reached directly (like video-library-api): send the raw owner access token and opt out
// of the owner-hub auth recovery/teardown, so a 401/403 from spark is surfaced to the caller, never turned
// into a session sign-out.
const authConfig = (): AxiosRequestConfig => {
  const token = localStorage.getItem('access_token') || '';
  return { _owsNoAuthRecovery: true, ...(token ? { headers: { Authorization: token } } : {}) };
};

const dataSourceUrl = (base: string, tenantId: number): string => `${base}/creatives/tenant/${tenantId}/data-source`;

type DataSourceReply = { ds_id?: string; source?: GameSourceBlob };

// Resolved blobs (so the catalogue is not re-fetched on every picker remount) and in-flight promises (so
// concurrent mounts share one request). Keyed by tenant + source id (+ view).
const resultCache = new Map<string, GameSourceBlob>();
const inFlight = new Map<string, Promise<GameSourceBlob>>();

const cacheKey = (tenantId: number, sourceId: string, view: GameSourceView | undefined): string =>
  view === undefined ? `${tenantId}:${sourceId}` : `${tenantId}:${sourceId}:${view}`;

/** A synchronously-available cached blob for (tenant, source, view), if one was already fetched: lets a remount
 *  paint immediately instead of flashing a spinner. */
export const cachedGameSource = (
  tenantId: number,
  sourceId: string,
  options: GameSourceOptions = {},
): GameSourceBlob | undefined => resultCache.get(cacheKey(tenantId, sourceId, options.view));

/**
 * Read ONE game data source through spark-creatives and return its raw upstream blob. Cached per
 * (tenant, source, view); concurrent callers share one request. Rejects on a network/HTTP error so the caller can
 * settle its loading state (never signs the user out; the request opts out of the auth teardown).
 *
 * `{ view: 'slim' }` reads the catalogue without its art (see {@link GameSourceView}); its records are then drawn with
 * {@link fetchGamesByIds} (or `useGameRecords` / `useGameThumb` / `GameThumb`). The FULL catalogue read also fills the
 * by-id record cache, so nothing it holds is read again by id.
 */
export const fetchGameSource = (
  tenantId: number,
  sourceId: string,
  options: GameSourceOptions = {},
): Promise<GameSourceBlob> => {
  const { view } = options;
  const key = cacheKey(tenantId, sourceId, view);
  const cached = resultCache.get(key);
  if (cached) return Promise.resolve(cached);
  const existing = inFlight.get(key);
  if (existing) return existing;
  const base = creativesBase();
  if (!base) return Promise.reject(new Error('creatives API not configured'));
  const request = axios
    .get<DataSourceReply>(dataSourceUrl(base, tenantId), {
      ...authConfig(),
      params: view === undefined ? { source_id: sourceId } : { source_id: sourceId, view },
    })
    .then((res) => {
      const source = res.data?.source;
      // Only cache a REAL source: a 200 with no source (a transient/misconfigured relay) must not stick an
      // empty {} in the cache for the whole session, which would kill pre-selection on edit with no retry.
      if (source == null) return {} as GameSourceBlob;
      resultCache.set(key, source);
      // Every game's full record: the by-id reads answer from it. Not the slim view, whose records carry no art.
      if (view === undefined && sourceId === GAME_CATALOG_SOURCE_ID) {
        keepRecords(lookupsOf(tenantId), recordsOf(gamesOfSource(source)));
      }
      return source;
    })
    .finally(() => {
      inFlight.delete(key);
    });
  inFlight.set(key, request);
  return request;
};

// ---------------------------------------------------------------------------------------------------------------
// Full game records by id
// ---------------------------------------------------------------------------------------------------------------

/** A promise the loader settles itself: the answer to one id, shared by everyone who asked for it meanwhile. */
interface Waiter {
  promise: Promise<void>;
  resolve: () => void;
  reject: (reason: unknown) => void;
}

const newWaiter = (): Waiter => {
  let resolve: () => void = () => undefined;
  let reject: (reason: unknown) => void = () => undefined;
  const promise = new Promise<void>((done, fail) => {
    resolve = () => done();
    reject = fail;
  });
  // Each caller handles its own copy (`Promise.all`); this one must never surface as an unhandled rejection.
  promise.catch(() => undefined);
  return { promise, resolve, reject };
};

/** One brand's by-id lookups. */
interface RecordLookups {
  /** Full records by game id, from every reply that carried them. */
  records: Map<string, GameSourceRecord>;
  /** Ids a successful read asked for and did not get: not in the catalogue, never asked for again. */
  missing: Set<string>;
  /** Ids asked for and not answered yet, queued or in a read, each with the waiter its askers share. */
  waiting: Map<string, Waiter>;
  /** Ids asked for this tick, sent together once it ends. */
  queued: string[];
  /** Whether the end of this tick is already scheduled to send `queued`. */
  flushing: boolean;
}

const lookups = new Map<number, RecordLookups>();

const lookupsOf = (tenantId: number): RecordLookups => {
  const existing = lookups.get(tenantId);
  if (existing) return existing;
  const created: RecordLookups = {
    records: new Map(),
    missing: new Set(),
    waiting: new Map(),
    queued: [],
    flushing: false,
  };
  lookups.set(tenantId, created);
  return created;
};

/** Reads waiting for a slot, every brand's, oldest first, and how many are out. */
const backlog: { tenantId: number; ids: string[] }[] = [];
let readsOut = 0;

/** The ids a read may carry: distinct, in the order given, and only tokens the service accepts. */
const askableIds = (ids: readonly string[]): string[] =>
  [...new Set(ids.map((id) => String(id)))].filter((id) => GAME_ID_TOKEN.test(id));

/** The usable game records of a reply's data: objects carrying an id. */
const recordsOf = (data: readonly unknown[]): GameSourceRecord[] =>
  data.filter(
    (row): row is GameSourceRecord =>
      typeof row === 'object' && row !== null && !Array.isArray(row) && gameKey(row as GameSourceRecord) !== '',
  );

/** Whether the cache answers `id`: held, or known not to exist. */
const isKnown = (state: RecordLookups, id: string): boolean => state.records.has(id) || state.missing.has(id);

/** Settle the askers of `id` when the cache now answers it. */
const settleIfKnown = (state: RecordLookups, id: string): boolean => {
  if (!isKnown(state, id)) return false;
  state.waiting.get(id)?.resolve();
  state.waiting.delete(id);
  return true;
};

/** Keep `records` (a record already held stays as it is, so what a reader holds does not change under it), then settle
 *  every asker the cache now answers: an older spark answers any read with the whole catalogue, so one reply can
 *  answer ids queued for other reads. */
const keepRecords = (state: RecordLookups, records: readonly GameSourceRecord[]): void => {
  records.forEach((record) => {
    const id = gameKey(record);
    if (!state.records.has(id)) state.records.set(id, record);
  });
  [...state.waiting.keys()].forEach((id) => settleIfKnown(state, id));
};

/** The held records of `ids`, in that order. */
const heldRecords = (state: RecordLookups | undefined, ids: readonly string[]): Map<string, GameSourceRecord> => {
  const byId = new Map<string, GameSourceRecord>();
  ids.forEach((id) => {
    const record = state?.records.get(id);
    if (record !== undefined) byId.set(id, record);
  });
  return byId;
};

/** One by-id read: the full records of `ids` (never empty). A 200 that carries no game array is a relay that failed, not
 *  an answer that these games are gone; taking it as one would remember every asked id as missing for the session. So is
 *  one whose array holds no game at all: spark answers an upstream read that failed without saying so with an empty list.
 *  A reply holding some games is an answer: an asked id it does not hold is gone. */
const readGames = async (tenantId: number, ids: readonly string[]): Promise<GameSourceRecord[]> => {
  const base = creativesBase();
  if (!base) throw new Error('creatives API not configured');
  const res = await axios.get<DataSourceReply>(dataSourceUrl(base, tenantId), {
    ...authConfig(),
    params: { source_id: GAME_CATALOG_SOURCE_ID, ids: ids.join(',') },
  });
  const data = res.data?.source?.data;
  if (!Array.isArray(data)) throw new Error('game records: the reply carries no games');
  const records = recordsOf(data);
  if (records.length === 0) throw new Error('game records: the reply carries no game at all');
  return records;
};

/** Start waiting reads while a slot is free. A read whose ids an earlier reply has answered since it was queued is
 *  narrowed to the rest, or dropped. */
const startReads = (): void => {
  while (readsOut < GAME_ID_READS_AT_ONCE) {
    const next = backlog.shift();
    if (next === undefined) return;
    const state = lookupsOf(next.tenantId);
    const ids = next.ids.filter((id) => !settleIfKnown(state, id));
    if (ids.length === 0) continue;
    readsOut += 1;
    void readGames(next.tenantId, ids)
      .then(
        (records) => {
          // Asked, read, not returned: the catalogue does not hold it. Remembered, so it is never asked again.
          const returned = new Set(records.map(gameKey));
          ids.forEach((id) => {
            if (!returned.has(id) && !state.records.has(id)) state.missing.add(id);
          });
          keepRecords(state, records);
        },
        (error: unknown) => {
          // Nothing of a failed read is kept: the next asker of these ids reads them again.
          ids.forEach((id) => {
            state.waiting.get(id)?.reject(error);
            state.waiting.delete(id);
          });
        },
      )
      .finally(() => {
        readsOut -= 1;
        startReads();
      });
  }
};

/** Send what this tick queued, in reads of at most {@link GAME_IDS_PER_READ} ids. */
const sendQueued = (tenantId: number): void => {
  const state = lookupsOf(tenantId);
  state.flushing = false;
  const ids = state.queued.splice(0);
  for (let at = 0; at < ids.length; at += GAME_IDS_PER_READ) {
    backlog.push({ tenantId, ids: ids.slice(at, at + GAME_IDS_PER_READ) });
  }
  startReads();
};

/**
 * The FULL records (art bundle included) of `ids` from the catalogue, by id, in the order asked: an id the catalogue
 * does not hold is simply absent, and so is one the service would refuse (not `^[A-Za-z0-9_-]{1,32}$`), which is
 * never sent. Duplicates collapse.
 *
 * Batched: the ids asked within one tick (by every caller) go out together once it ends, in reads of at most
 * {@link GAME_IDS_PER_READ} ids, at most {@link GAME_ID_READS_AT_ONCE} reads out at once. An id already held, or known
 * not to exist, is answered without a read; one already asked for joins that read. Every record a reply carries is
 * kept (an older spark ignores `ids` and answers the whole catalogue, which fills the cache in one go), and an id a
 * successful reply did not return is remembered as missing, never asked for again. A failed read keeps nothing and
 * rejects everyone waiting on it; the next ask reads again. A reply that carries no game at all is a failed read too
 * (spark's answer when its upstream read failed without saying so), never every asked game being gone.
 */
export const fetchGamesByIds = (
  tenantId: number,
  ids: readonly string[],
): Promise<ReadonlyMap<string, GameSourceRecord>> => {
  const asked = askableIds(ids);
  const state = lookupsOf(tenantId);
  const waits: Promise<void>[] = [];
  asked.forEach((id) => {
    if (isKnown(state, id)) return;
    let waiter = state.waiting.get(id);
    if (waiter === undefined) {
      waiter = newWaiter();
      state.waiting.set(id, waiter);
      state.queued.push(id);
    }
    waits.push(waiter.promise);
  });
  if (state.queued.length > 0 && !state.flushing) {
    state.flushing = true;
    setTimeout(() => sendQueued(tenantId), 0);
  }
  const answer = (): ReadonlyMap<string, GameSourceRecord> => heldRecords(state, asked);
  return waits.length === 0 ? Promise.resolve(answer()) : Promise.all(waits).then(answer);
};

/** What the by-id cache already knows of some ids, without reading anything. */
export interface CachedGameRecords {
  /** The held full records of the ids, in the order asked. */
  byId: ReadonlyMap<string, GameSourceRecord>;
  /** Whether every id is answered: held, known not to exist, or not an id the service accepts. False means a read
   *  ({@link fetchGamesByIds}) would still learn something. */
  complete: boolean;
}

/** The by-id cache's answer for `ids` right now: lets a remount paint at once instead of flashing a placeholder. */
export const cachedGameRecords = (tenantId: number, ids: readonly string[]): CachedGameRecords => {
  const state = lookups.get(tenantId);
  const asked = askableIds(ids);
  return {
    byId: heldRecords(state, asked),
    complete: asked.every((id) => state !== undefined && isKnown(state, id)),
  };
};

/** One game's held full record, if a read has brought it in. */
export const cachedGameRecord = (tenantId: number, id: string): GameSourceRecord | undefined =>
  lookups.get(tenantId)?.records.get(String(id));
